import { Role } from "@ciptex/notified";
import { Paragraph, Box, Button } from "@twilio-paste/core";
import { Dispatch, FC, SetStateAction } from "react";
import { appStateTypes } from "../../../core/appReducer";
import { ToggleAudioButton } from "../../Buttons/ToggleAudioButton/ToggleAudioButton";
import { ToggleVideoButton } from "../../Buttons/ToggleVideoButton/ToggleVideoButton";
import { LocalVideoPreview } from "./LocalVideoPreview/LocalVideoPreview";

interface DeviceAudioTabType {
    state: appStateTypes,
    isAcquiringLocalTracks: boolean,
	ENABLE_VIDEO: boolean,
	setDeviceSettingsOpen: Dispatch<SetStateAction<boolean>>,
	connect: (webrtc?: boolean | undefined) => void,
	isLoadingConferenceDetails: boolean
}

export const DeviceAudioTab: FC<DeviceAudioTabType> = ({ state, isAcquiringLocalTracks, ENABLE_VIDEO, setDeviceSettingsOpen, connect, isLoadingConferenceDetails }: DeviceAudioTabType) => {
	const shouldDisableButton = () => {
		if (isAcquiringLocalTracks) {
			return true;
		}

		// If we'll be starting the room (as opposed to joining) we need to wait for conference details
		// to finish loading. For now we're just checking if the user's role is a host or admin (these are
		// the only roles that can start the room) but we should also check if the room has already been started.

		switch (state.participantType) {
		case Role.NOTIFIED_ADMIN:
		case Role.NOTIFIED_HOST:
			// For admins and hosts, disable the button if the conference has not finished loading
			return isLoadingConferenceDetails;

		default:
			return false;
		}
	};

	return (<>
		<Paragraph>Access the Conference Call without a telephone. Use your device microphone and speaker.</Paragraph>
		<Box display="flex" flex={1} flexDirection={["column", "row", "row"]} columnGap="space60" justifyContent="space-between" alignContent="space-between" marginY="space80">
			<Box width={["100%", "50%", "50%"]} marginBottom={["space80", "space0", "space0"]}>
				<LocalVideoPreview identity={state.participantIdentity} />
			</Box>
			<Box display="flex" flex={1} flexDirection="column" alignSelf="end" justifySelf="end">
				<Box>
					<ToggleAudioButton disabled={isAcquiringLocalTracks} />
				</Box>
				<Box>
					{ENABLE_VIDEO && <ToggleVideoButton disabled={isAcquiringLocalTracks} />}
				</Box>
			</Box>
		</Box>
		<Box display="flex" flexDirection="row" alignContent="space-between" justifyContent="space-between">
			<Button variant="secondary" onClick={() => setDeviceSettingsOpen(true)} disabled={isAcquiringLocalTracks}>Audio{ENABLE_VIDEO && " and Video"} Settings</Button>
			<Button variant="primary" data-cy-join-now onClick={() => connect()} disabled={shouldDisableButton()}>
				{isLoadingConferenceDetails ?
					"Please wait..." :
					state.participantType === Role.NOTIFIED_ADMIN || state.participantType === Role.NOTIFIED_HOST ? "Create Event" : "Join Conference"
				}
			</Button>
		</Box>
	</>);
}
